import { FC, useMemo, useState } from 'react';
import { createStyles, makeStyles } from '@material-ui/core';
import { Typography } from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import DraftsIcon from '@material-ui/icons/Drafts';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import { format } from 'date-fns';
import classNames from 'classnames';

import { METRICS_TABS, MetricsTabId } from './constants';
import {
  MetricsNotification,
  NOTIFICATION_SEVERITY,
  tabLabel,
} from './notificationsMock';

const useStyles = makeStyles(() =>
  createStyles({
    feed: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      background: '#FFFFFF',
    },
    feedHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
      padding: '16px 16px 12px',
      borderBottom: '1px solid #E4E0D8',
    },
    feedTitle: {
      fontSize: 16,
      fontWeight: 600,
      lineHeight: '22px',
      color: '#202226',
    },
    feedCount: {
      color: '#5C6168',
      fontSize: 13,
      lineHeight: '18px',
      marginTop: 2,
    },
    list: {
      overflowY: 'auto',
      maxHeight: 420,
    },
    item: {
      position: 'relative',
      padding: '12px 16px',
      borderBottom: '1px solid #EFEDEB',
      textAlign: 'left',

      '&:hover $rowActions, &:focus-within $rowActions': {
        opacity: 1,
        pointerEvents: 'auto',
      },

      '&:hover $time, &:focus-within $time': {
        visibility: 'hidden',
      },
    },
    itemBody: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    rowActions: {
      position: 'absolute',
      top: 0,
      right: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      opacity: 0,
      pointerEvents: 'none',
    },
    hoverButton: {
      width: 24,
      height: 24,
      padding: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px solid #C8C4BC',
      background: '#FFFFFF',
      color: '#202226',
      borderRadius: 2,
      cursor: 'pointer',

      '&:hover': {
        background: '#F7F5F3',
      },
    },
    hoverIcon: {
      fontSize: 16,
    },
    itemTitleChecked: {
      fontWeight: 500,
      color: '#8B929A',
    },
    messageChecked: {
      color: '#A4A9AE',
    },
    bulkActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexShrink: 0,
    },
    markAll: {
      border: 0,
      background: 'transparent',
      color: '#005FFA',
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '16px',
      cursor: 'pointer',
      fontFamily: 'inherit',
      padding: '4px 0',
      whiteSpace: 'nowrap',

      '&:hover': {
        textDecoration: 'underline',
      },

      '&:disabled': {
        color: '#8B929A',
        cursor: 'default',
        textDecoration: 'none',
      },
    },
    meta: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap',
      minHeight: 24,
    },
    severity: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '16px',
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      flexShrink: 0,
    },
    source: {
      color: '#5C6168',
      fontSize: 12,
      lineHeight: '16px',
    },
    time: {
      marginLeft: 'auto',
      color: '#8B929A',
      fontSize: 12,
      lineHeight: '16px',
    },
    itemTitle: {
      color: '#202226',
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '20px',
    },
    message: {
      color: '#5C6168',
      fontSize: 13,
      lineHeight: '18px',
    },
    empty: {
      padding: '28px 16px',
      color: '#5C6168',
      fontSize: 14,
      lineHeight: '20px',
    },
    viewAll: {
      border: 0,
      borderTop: '1px solid #E4E0D8',
      background: '#F7F5F3',
      color: '#202226',
      fontSize: 13,
      fontWeight: 600,
      lineHeight: '18px',
      padding: '12px 16px',
      cursor: 'pointer',
      fontFamily: 'inherit',
      textAlign: 'left',

      '&:hover': {
        background: '#E4E0D8',
      },
    },
    page: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
    },
    pageHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
    },
    summary: {
      color: '#5C6168',
      fontSize: 14,
      lineHeight: '20px',
    },
    filters: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
    },
    filter: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      height: 32,
      padding: '0 12px',
      background: '#FFFFFF',
      border: '1px solid #C8C4BC',
      borderRadius: 2,
      color: '#202226',
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
      fontFamily: 'inherit',

      '&:hover': {
        background: '#F7F5F3',
      },
    },
    filterSelected: {
      color: '#FFFFFF',
      background: 'linear-gradient(0deg, #005FFA -20.98%, #00B5FF 140.22%)',
      borderColor: 'transparent',

      '&:hover': {
        color: '#FFFFFF',
        background: 'linear-gradient(0deg, #005FFA -20.98%, #00B5FF 140.22%)',
      },
    },
    filterCount: {
      minWidth: 18,
      height: 18,
      padding: '0 5px',
      borderRadius: 9,
      background: '#DC5E8A',
      color: '#FFFFFF',
      fontSize: 11,
      fontWeight: 700,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
    },
    filterCountSelected: {
      background: '#FFFFFF',
      color: '#005FFA',
    },
    cards: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
    card: {
      position: 'relative',
      background: '#FFFFFF',
      border: '1px solid #D8D4CE',
      borderRadius: 2,
      padding: '14px 16px',

      '&:hover $rowActions, &:focus-within $rowActions': {
        opacity: 1,
        pointerEvents: 'auto',
      },

      '&:hover $time, &:focus-within $time': {
        visibility: 'hidden',
      },
    },
    itemRead: {
      background: '#FFFFFF',
    },
    itemUnread: {
      background: '#E4DDD4',
    },
  })
);

const formatWhen = (timestamp: number) =>
  format(new Date(timestamp), 'MMM d, H:mm');

const uncheckedCount = (items: InboxNotification[]) =>
  items.filter((item) => !item.checked).length;

const uncheckedLabel = (count: number) => {
  if (count === 0) {
    return 'All read';
  }

  return count === 1 ? '1 unread' : `${count} unread`;
};

export interface InboxNotification extends MetricsNotification {
  checked: boolean;
}

interface NotificationRowProps {
  notification: InboxNotification;
  showSource?: boolean;
  onToggleChecked: (id: string) => void;
  onDelete: (id: string) => void;
  className: string;
}

const NotificationRow: FC<NotificationRowProps> = ({
  notification,
  showSource,
  onToggleChecked,
  onDelete,
  className,
}) => {
  const classes = useStyles();
  const severity = NOTIFICATION_SEVERITY[notification.severity];
  const textColor = notification.checked ? '#8B929A' : severity.color;

  const actionLabel = notification.checked ? 'Mark as unread' : 'Mark as read';

  return (
    <div
      className={classNames(
        className,
        notification.checked ? classes.itemRead : classes.itemUnread
      )}
    >
      <div className={classes.itemBody}>
        <div className={classes.meta}>
          <span className={classes.severity} style={{ color: textColor }}>
            <span
              className={classes.dot}
              style={{ background: severity.color }}
            />
            {severity.label}
          </span>
          {showSource && (
            <span className={classes.source}>
              {tabLabel(notification.tabId)}
            </span>
          )}
          <span className={classes.time}>
            {formatWhen(notification.timestamp)}
          </span>
          <span className={classes.rowActions}>
            <button
              type='button'
              className={classes.hoverButton}
              aria-label={actionLabel}
              title={actionLabel}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onToggleChecked(notification.id)}
            >
              {notification.checked ? (
                <MailOutlineIcon className={classes.hoverIcon} />
              ) : (
                <DraftsIcon className={classes.hoverIcon} />
              )}
            </button>
            <button
              type='button'
              className={classes.hoverButton}
              aria-label='Delete'
              title='Delete'
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onDelete(notification.id)}
            >
              <DeleteOutlineIcon className={classes.hoverIcon} />
            </button>
          </span>
        </div>
        <div
          className={classNames(classes.itemTitle, {
            [classes.itemTitleChecked]: notification.checked,
          })}
        >
          {notification.title}
        </div>
        <div
          className={classNames(classes.message, {
            [classes.messageChecked]: notification.checked,
          })}
        >
          {notification.message}
        </div>
      </div>
    </div>
  );
};

interface MarkAllButtonProps {
  notifications: InboxNotification[];
  onMarkChecked: (ids: string[]) => void;
}

const MarkAllButton: FC<MarkAllButtonProps> = ({
  notifications,
  onMarkChecked,
}) => {
  const classes = useStyles();
  const ids = notifications
    .filter((item) => !item.checked)
    .map((item) => item.id);

  return (
    <button
      type='button'
      className={classes.markAll}
      disabled={ids.length === 0}
      onClick={() => onMarkChecked(ids)}
    >
      Mark all as read
    </button>
  );
};

interface DeleteAllButtonProps {
  notifications: InboxNotification[];
  onDeleteMany: (ids: string[]) => void;
}

const DeleteAllButton: FC<DeleteAllButtonProps> = ({
  notifications,
  onDeleteMany,
}) => {
  const classes = useStyles();
  const ids = notifications.map((item) => item.id);

  return (
    <button
      type='button'
      className={classes.markAll}
      disabled={ids.length === 0}
      onClick={() => onDeleteMany(ids)}
    >
      Delete all
    </button>
  );
};

interface NotificationFeedProps {
  title: string;
  notifications: InboxNotification[];
  onToggleChecked: (id: string) => void;
  onMarkChecked: (ids: string[]) => void;
  onDelete: (id: string) => void;
  onDeleteMany: (ids: string[]) => void;
  onViewAll?: () => void;
}

export const NotificationFeed: FC<NotificationFeedProps> = ({
  title,
  notifications,
  onToggleChecked,
  onMarkChecked,
  onDelete,
  onDeleteMany,
  onViewAll,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.feed}>
      <div className={classes.feedHeader}>
        <div>
          <Typography className={classes.feedTitle}>{title}</Typography>
          <div className={classes.feedCount}>
            {uncheckedLabel(uncheckedCount(notifications))}
          </div>
        </div>
        <div className={classes.bulkActions}>
          <MarkAllButton
            notifications={notifications}
            onMarkChecked={onMarkChecked}
          />
          <DeleteAllButton
            notifications={notifications}
            onDeleteMany={onDeleteMany}
          />
        </div>
      </div>
      {notifications.length === 0 ? (
        <div className={classes.empty}>No notifications for this section.</div>
      ) : (
        <div className={classes.list}>
          {notifications.map((notification) => (
            <NotificationRow
              key={notification.id}
              className={classes.item}
              notification={notification}
              onToggleChecked={onToggleChecked}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
      {onViewAll && (
        <button type='button' className={classes.viewAll} onClick={onViewAll}>
          All notifications
        </button>
      )}
    </div>
  );
};

type NotificationFilter = 'all' | MetricsTabId;

interface AllNotificationsProps {
  notifications: InboxNotification[];
  onToggleChecked: (id: string) => void;
  onMarkChecked: (ids: string[]) => void;
  onDelete: (id: string) => void;
  onDeleteMany: (ids: string[]) => void;
}

export const AllNotifications: FC<AllNotificationsProps> = ({
  notifications,
  onToggleChecked,
  onMarkChecked,
  onDelete,
  onDeleteMany,
}) => {
  const classes = useStyles();
  const [filter, setFilter] = useState<NotificationFilter>('all');

  const visible = useMemo(() => {
    if (filter === 'all') {
      return notifications;
    }

    return notifications.filter((item) => item.tabId === filter);
  }, [filter, notifications]);

  const uncheckedTotal = uncheckedCount(notifications);

  return (
    <div className={classes.page}>
      <div className={classes.pageHeader}>
        <Typography className={classes.summary}>
          {uncheckedLabel(uncheckedTotal)} across health checks
        </Typography>
        <div className={classes.bulkActions}>
          <MarkAllButton
            notifications={visible}
            onMarkChecked={onMarkChecked}
          />
          <DeleteAllButton
            notifications={visible}
            onDeleteMany={onDeleteMany}
          />
        </div>
      </div>
      <div
        className={classes.filters}
        role='group'
        aria-label='Filter notifications'
      >
        <FilterButton
          label='All'
          count={uncheckedTotal}
          selected={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        {METRICS_TABS.map((tab) => (
          <FilterButton
            key={tab.id}
            label={tab.label}
            count={uncheckedCount(
              notifications.filter((item) => item.tabId === tab.id)
            )}
            selected={filter === tab.id}
            onClick={() => setFilter(tab.id)}
          />
        ))}
      </div>
      {visible.length === 0 ? (
        <Typography className={classes.summary}>
          No notifications in this section.
        </Typography>
      ) : (
        <div className={classes.cards}>
          {visible.map((notification) => (
            <NotificationRow
              key={notification.id}
              className={classes.card}
              notification={notification}
              showSource
              onToggleChecked={onToggleChecked}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface FilterButtonProps {
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
}

const FilterButton: FC<FilterButtonProps> = ({
  label,
  count,
  selected,
  onClick,
}) => {
  const classes = useStyles();

  return (
    <button
      type='button'
      aria-pressed={selected}
      className={classNames(classes.filter, {
        [classes.filterSelected]: selected,
      })}
      onClick={onClick}
    >
      {label}
      {count > 0 && (
        <span
          className={classNames(classes.filterCount, {
            [classes.filterCountSelected]: selected,
          })}
        >
          {count}
        </span>
      )}
    </button>
  );
};
